import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthContextType, AuthResponse } from '../types/auth';
import { authAPI } from '../services/api';
import api from '../services/api';
import { clearServiceWorkerConfig, unsubscribeFromPush } from '../services/push';
import { getErrorMessage } from '../utils/errorMessage';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * A /profile válaszából összerakott, teljes user objektum. A képességeket
   * (isAdmin/isCoach/isPlayer/isParent) mindig a szerver mondja meg - a
   * korábbi objektumból csak azok a mezők maradnak, amiket a /profile nem ad
   * vissza (pl. korosztályok).
   */
  const mergeProfile = (previous: User | null, profile: any): User => ({
    ...(previous ?? ({} as User)),
    id: profile.id,
    email: profile.email,
    name: profile.name,
    isAdmin: profile.isAdmin === true,
    isCoach: profile.isCoach === true,
    isPlayer: profile.isPlayer === true,
    isParent: profile.isParent === true,
    isGuest: profile.isGuest === true,
    guestTeam: profile.guestTeam ?? null,
    createdAt: profile.createdAt ?? previous?.createdAt ?? '',
  });

  /**
   * A localStorage-ban tárolt user egy korábbi appverzió alakját is őrizheti
   * (pl. a régi, kizárólagos szerepkör-mezőt), és az admin időközben módosít-
   * hatta a jogosultságainkat. Ezért a szerverről frissítjük a képességeket -
   * így a menü sosem egy elavult pillanatképből épül fel, és nem kell hozzá
   * ki-be lépni. Hálózati hiba esetén marad a tárolt állapot.
   */
  const syncUserFromServer = async (previous: User | null): Promise<void> => {
    try {
      const response = await api.get('/profile');
      const synced = mergeProfile(previous, response.data);
      setUser(synced);
      localStorage.setItem('user', JSON.stringify(synced));
    } catch (error) {
      console.error('A felhasználó frissítése nem sikerült:', error);
    }
  };

  useEffect(() => {
    // Check for existing session on app load
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!storedToken || !storedUser) {
      setLoading(false);
      return;
    }

    let restored: User | null = null;
    try {
      restored = JSON.parse(storedUser) as User;
    } catch {
      restored = null;
    }

    setToken(storedToken);
    setUser(restored);
    setLoading(false);

    void syncUserFromServer(restored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await authAPI.login({ email, password });

      setToken(response.token);
      setUser(response.user);

      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'A bejelentkezés nem sikerült.'));
    }
  };

  /**
   * Meglévő token és felhasználó beállítása bejelentkezés nélkül. A meghívó
   * elfogadása után használjuk, ahol a szerver már adott munkamenetet.
   */
  const applySession = (session: AuthResponse): void => {
    setToken(session.token);
    setUser(session.user);
    localStorage.setItem('token', session.token);
    localStorage.setItem('user', JSON.stringify(session.user));
  };

  const logout = async (): Promise<void> => {
    // A leiratkozás még a token törlése előtt fut le, hogy a szerver is
    // értesüljön róla - így közös eszközön nem érkezik push a kilépett fiókhoz.
    try {
      await unsubscribeFromPush();
    } catch (error) {
      console.error('A push leiratkozás sikertelen:', error);
    }

    await clearServiceWorkerConfig();

    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const refreshUser = async (): Promise<void> => {
    await syncUserFromServer(user);
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    applySession,
    refreshUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};